import { createFileRoute, useParams, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";
import { CheckCircle2, XCircle, Loader2, Download, ShieldCheck, AlertTriangle } from "lucide-react";
import { verifyCertificate, downloadCertificateFromUrl } from "@/lib/api";

export const Route = createFileRoute("/verify/$certCode")({
  head: () => ({
    meta: [
      { title: "Verify certificate — Dev-Hub" },
      {
        name: "description",
        content:
          "Verify the authenticity of a Dev-Hub certificate by its unique credential code.",
      },
      { property: "og:title", content: "Verify certificate — Dev-Hub" },
      {
        property: "og:description",
        content:
          "Verify the authenticity of a Dev-Hub certificate by its unique credential code.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
    // No canonical — this is a per-record page with personal data; a canonical
    // on a noindex page is a mixed signal.
  }),
  component: VerifyCertificatePage,
});

interface VerifySearch {
  download?: string;
}

function VerifyCertificatePage() {
  const { certCode } = useParams({ from: "/verify/$certCode" });
  const search = useSearch({ from: "/verify/$certCode" }) as VerifySearch;

  const { data, isLoading, error } = useQuery({
    queryKey: ["verify-cert", certCode],
    queryFn: () => verifyCertificate(certCode),
    retry: false,
  });

  const handleDownload = async () => {
    if (!data?.certificate_url) return;
    try {
      await downloadCertificateFromUrl(data.certificate_url, `certificate-${certCode}.pdf`);
    } catch {
      // A cross-origin static host may block the blob fetch (CORS). Fall back to
      // opening the PDF in a new tab so the user can still save it, without
      // navigating away from the verification page.
      window.open(data.certificate_url, "_blank", "noopener,noreferrer");
    }
  };

  useEffect(() => {
    if (search.download === "true" && data?.valid && data.certificate_url) {
      handleDownload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.download, data]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin text-slate-800" />
          <p className="text-slate-500 font-medium tracking-wide">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (error?.message === "RATE_LIMIT_EXCEEDED") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4 font-sans">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border p-8 text-center">
          <AlertTriangle className="h-16 w-16 text-rose-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Too Many Requests</h2>
          <p className="text-slate-500">
            You have made too many verification requests. Please try again shortly.
          </p>
        </div>
      </div>
    );
  }

  const isValid = data?.valid === true;
  const isRevoked = data?.status === "revoked";

  if (!isValid && !isRevoked) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4 font-sans">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border p-8 text-center">
          <XCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Record Not Found</h2>
          <p className="text-slate-500">No valid certificate matches the code "{certCode}".</p>
        </div>
      </div>
    );
  }

  if (isRevoked) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4 font-sans">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border p-8 text-center">
          <XCircle className="h-16 w-16 text-rose-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Revoked Credential</h2>
          <p className="text-slate-500">
            This certificate has been officially revoked by the institution.
          </p>
        </div>
      </div>
    );
  }

  // VALID CERTIFICATE
  const issueDate = data.issued_at
    ? new Date(data.issued_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "N/A";

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center py-12 px-4 font-sans print:bg-white print:p-0">
      {/* Action Bar (Hidden in print) */}
      <div className="max-w-[1000px] w-full flex flex-col sm:flex-row sm:justify-between items-start sm:items-center gap-3 mb-6 print:hidden">
        <div className="flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full font-medium text-sm">
          <CheckCircle2 className="h-5 w-5" />
          Verified Official Credential
        </div>
        {data.certificate_url && (
          <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={handleDownload}>
            <Download className="mr-2 h-4 w-4" />
            Download Original PDF
          </Button>
        )}
      </div>

      {/* Textual summary — the authoritative snapshot data, identical to what the PDF renders */}
      <div className="w-full max-w-[1000px] mb-6 bg-white rounded-2xl shadow-sm border p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex h-12 w-12 shrink-0 rounded-xl bg-slate-900 text-white items-center justify-center">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-slate-400 font-medium mb-1">
              Awarded to
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold font-serif text-slate-900 leading-tight">
              {data.recipient_name}
            </h1>
            <div className="text-slate-500 text-sm italic mt-1 mb-3">
              has successfully completed
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-slate-800 leading-snug">
              {data.certificate_title}
            </h2>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 text-sm">
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wide">Issued</div>
                <div className="font-medium text-slate-800">{issueDate}</div>
              </div>
              {data.custom_hours ? (
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wide">
                    Training Hours
                  </div>
                  <div className="font-medium text-slate-800">{data.custom_hours} hours</div>
                </div>
              ) : null}
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wide">Credential ID</div>
                <div className="font-mono text-xs font-medium text-slate-800 break-all">
                  {certCode}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wide">Verify at</div>
                <div className="font-medium text-slate-800 break-all">
                  {window.location.origin}/verify/{certCode}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Official certificate — embed the actual generated PDF so what is verified here is
          byte-for-byte identical to what the recipient downloads. */}
      {data.certificate_url ? (
        <div className="w-full max-w-[1000px] print:hidden">
          <div className="mb-2 text-xs uppercase tracking-widest text-slate-400 font-medium">
            Official Certificate
          </div>
          <div className="w-full bg-white aspect-[1.414/1] rounded-2xl shadow-2xl border overflow-hidden">
            <iframe
              src={`${data.certificate_url}#toolbar=0&navpanes=0&view=FitH`}
              title={`Certificate ${certCode}`}
              className="w-full h-full"
            />
          </div>
          <p className="mt-3 text-center text-xs text-slate-400">
            If the certificate does not display,{" "}
            <button
              type="button"
              onClick={handleDownload}
              className="underline hover:text-slate-600"
            >
              download the original PDF
            </button>
            .
          </p>
        </div>
      ) : (
        <div className="w-full max-w-[1000px] bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center text-sm text-amber-800">
          The certificate document is still being generated. Please check back shortly.
        </div>
      )}
    </div>
  );
}
